import { Component } from 'react';

/**
 * Keeps a failure in CrizacAI from becoming a blank white page.
 *
 * Without a boundary, an error thrown while rendering — or a failed lazy
 * import of the page itself — unmounts the whole React tree and leaves nothing
 * on screen and nothing to report. This shows what went wrong and offers the
 * two things that fix the usual causes: reloading (a stale dev-server module
 * or a failed chunk) and clearing the chats saved in this browser (a saved
 * conversation the current version cannot draw).
 *
 * Styled inline on purpose: it has to work when the page's CSS did not load.
 */
const SAVED_KEYS = ['crizac.chat.conversations.v1', 'crizac.chat.saved.v1'];

export class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[CrizacAI] render failed:', error, info?.componentStack);
  }

  clearSavedAndReload = () => {
    for (const key of SAVED_KEYS) {
      try { localStorage.removeItem(key); } catch { /* storage blocked */ }
    }
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div role="alert" style={{ font: '15px/1.5 system-ui, sans-serif', maxWidth: 560, margin: '12vh auto', padding: '0 20px', color: '#14181f' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>CrizacAI couldn't load</h2>
        <p style={{ margin: '0 0 14px', color: '#4b5563' }}>
          Something went wrong while drawing the page. Reloading usually fixes it; if it keeps happening,
          clearing the chats saved in this browser will.
        </p>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#f2f4f7', padding: '10px 12px', borderRadius: 8, fontSize: 12.5, margin: '0 0 16px' }}>
          {String(error?.message ?? error)}
        </pre>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ background: '#ef5a28', color: '#fff', border: 0, borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', marginRight: 8 }}
        >
          Reload
        </button>
        <button
          type="button"
          onClick={this.clearSavedAndReload}
          style={{ background: '#fff', color: '#14181f', border: '1px solid #d0d5dd', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}
        >
          Clear saved chats and reload
        </button>
      </div>
    );
  }
}

/** Shown while the chat page's code loads, instead of nothing. */
export function PageLoading() {
  return (
    <div style={{ font: '14px system-ui, sans-serif', color: '#7c8592', display: 'grid', placeItems: 'center', height: '100vh' }}>
      Loading CrizacAI…
    </div>
  );
}
